import { AssistantSchema, TOOL_REGISTRY } from "@/lib/index.js";
// import { sanitizeMetadata } from "@/lib/openai/assistantUtils";
import useAssistantStore from "@/store/assistantStore";
import useAuthStore from "@/store/authStore";
import { zodResolver } from "@hookform/resolvers/zod";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";

export const INITIAL_FORM_STATE = {
  name: "",
  description: "",
  instructions: "",
  model: "gpt-4.1-mini",
  tools: [],
  tool_resources: {},
  temperature: 0.7,
  top_p: 1,
  response_format: "text",
  metadata: {},
};

export const VALID_TOOL_TYPES = {
  FILE_SEARCH: "file_search",
  CODE_INTERPRETER: "code_interpreter",
  FUNCTION: "function",
  RETRIEVAL: "retrieval",
};

export const DIALOG_INITIAL_STATE = {
  fileSearch: false,
  codeInterpreter: false,
  functionWriter: false,
};

export const getFieldValue = (value, defaultValue) => {
  if (value === undefined || value === null) {
    return defaultValue;
  }
  return value;
};

export function useAssistantForm(assistant) {
  const { userId } = useAuthStore.getState();
  const {
    updateAssistant,
    updateAssistantTools,
    setCurrentAssistant,
    currentAssistant,
  } = useAssistantStore();

  const [isSaving, setIsSaving] = useState(false);
  const [isEditingInstructions, setIsEditingInstructions] = useState(false);
  const [pendingInstructions, setPendingInstructions] = useState(null);
  const [pendingName, setPendingName] = useState(null);
  const [pendingDescription, setPendingDescription] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSuggestingTools, setIsSuggestingTools] = useState(false);

  // Get initial response format
  const initialResponseFormat =
    assistant?.response_format?.type || assistant?.response_format || "text";

  const {
    control,
    reset,
    setValue,
    watch,
    formState: { errors, isDirty },
  } = useForm({
    resolver: zodResolver(AssistantSchema),
    defaultValues: {
      ...INITIAL_FORM_STATE,
      ...assistant,
      response_format: initialResponseFormat,
      instructions: getFieldValue(assistant?.instructions, ""),
      name: getFieldValue(assistant?.name, ""),
      description: getFieldValue(assistant?.description, ""),
      model: getFieldValue(assistant?.model, "gpt-4.1-mini"),
      temperature: getFieldValue(assistant?.temperature, 0.7),
      top_p: getFieldValue(assistant?.top_p, 1),
    },
    mode: "all",
  });

  const [tools, setTools] = useState(assistant?.tools || []);

  useEffect(() => {
    if (assistant?.tools) {
      setTools(assistant?.tools);
      setValue("tools", assistant?.tools);
    }
  }, [assistant?.tools, setValue]);

  useEffect(() => {
    if (assistant?.instructions !== undefined) {
      setValue("instructions", assistant.instructions);
    }
  }, [assistant?.instructions, setValue]);

  // Set initial values for pending fields
  useEffect(() => {
    if (assistant) {
      setPendingName(assistant.name || "");
      setPendingDescription(assistant.description || "");
      setPendingInstructions(assistant.instructions || "");
    }
  }, [assistant]);

  const [files, setFiles] = useState({
    vector: [],
    codeInterpreter: [],
  });
  const [isGeneratingInstructions, setIsGeneratingInstructions] =
    useState(false);
  const [dialogState, setDialogState] = useState({
    fileSearch: false,
    codeInterpreter: false,
    functionWriter: false,
  });

  // Handle field updates without automatic saving
  const handleFieldUpdate = useCallback(
    (field, value) => {
      setValue(field, value);
      setHasUnsavedChanges(true);

      if (field === "name") {
        setPendingName(value);
      } else if (field === "description") {
        setPendingDescription(value);
      } else if (field === "instructions") {
        setPendingInstructions(value);
        setIsEditingInstructions(true);
      }
    },
    [setValue]
  );

  // Save all pending changes at once
  const saveAllChanges = useCallback(async () => {
    if (!hasUnsavedChanges) {
      return;
    }

    try {
      setIsSaving(true);

      // Get the current assistant ID (should be Supabase UUID)
      const assistantId = currentAssistant?.id;

      if (!assistantId) {
        throw new Error("No valid assistant ID found");
      }

      // Prepare update data with all pending changes
      const updateData = {
        metadata: {
          ...(currentAssistant?.metadata || {}),
          updated_at: new Date().toISOString(),
          user_id: userId,
        },
      };

      // Add pending fields if they have changed
      if (pendingName !== null && pendingName !== currentAssistant?.name) {
        updateData.name = pendingName;
      }

      if (
        pendingDescription !== null &&
        pendingDescription !== currentAssistant?.description
      ) {
        updateData.description = pendingDescription;
      }

      if (
        pendingInstructions !== null &&
        pendingInstructions !== currentAssistant?.instructions
      ) {
        updateData.instructions = pendingInstructions;
      }

      // Update via store function to keep local state in sync
      const updatedAssistant = await updateAssistant(assistantId, updateData);

      // Direct Supabase update to ensure persistence
      try {
        // Prepare Supabase update data
        const supabaseUpdateData = {
          updated_at: new Date().toISOString(),
        };

        // Add the fields being updated
        if (updateData.name !== undefined) {
          supabaseUpdateData.name = updateData.name;
        }

        if (updateData.description !== undefined) {
          supabaseUpdateData.description = updateData.description;
        }

        if (updateData.instructions !== undefined) {
          supabaseUpdateData.instructions = updateData.instructions;
        }

        // Always update metadata
        supabaseUpdateData.metadata = {
          ...(currentAssistant?.metadata || {}),
          updated_at: new Date().toISOString(),
          user_id: userId,
        };

        // Import supabase directly to ensure we have access
        const { supabase } = await import("@/lib/supabase");

        const { error } = await supabase
          .from("assistants")
          .update(supabaseUpdateData)
          .eq("id", assistantId)
          .select()
          .single();

        if (error) {
          console.error("Direct Supabase update error:", error);
        }
      } catch (supabaseError) {
        console.error("Supabase update exception:", supabaseError);
      }

      setCurrentAssistant(updatedAssistant);
      setIsEditingInstructions(false);
      setHasUnsavedChanges(false);
    } catch (error) {
      console.error(`Error saving changes:`, error);
    } finally {
      setIsSaving(false);
    }
  }, [
    currentAssistant,
    hasUnsavedChanges,
    pendingName,
    pendingDescription,
    pendingInstructions,
    setValue,
    updateAssistant,
    setCurrentAssistant,
    userId,
  ]);

  // Watch for changes in form values
  const formValues = watch();

  // Files initialization - simplified since we no longer use OpenAI Assistants API
  useEffect(() => {
    // Initialize files state with empty arrays since we handle files differently now
    setFiles({
      vector: [],
      codeInterpreter: [],
    });
  }, [assistant]);

  const handleToolToggle = async (toolId) => {
    try {
      if (!currentAssistant) {
        throw new Error("No current assistant selected");
      }

      // Get current tools array, ensuring it's initialized even if the assistant doesn't have tools
      let newTools = [...(currentAssistant.tools || [])];

      // Get the assistant ID (should be Supabase UUID)
      const assistantId = currentAssistant.id;

      if (!assistantId) {
        throw new Error("No valid assistant ID found");
      }

      // Store all current form values before API call
      const currentFormData = {
        name: pendingName || formValues.name,
        description: pendingDescription || formValues.description,
        instructions:
          pendingInstructions ||
          formValues.instructions ||
          currentAssistant.instructions,
        model: formValues.model,
        temperature: formValues.temperature,
        top_p: formValues.top_p,
      };

      // Check if the tool is already enabled
      const isToolEnabled = newTools.some(
        (t) => t.type === "function" && t.function?.name === toolId
      );

      // If tool is enabled, remove it; otherwise, add it
      if (isToolEnabled) {
        // Remove the function tool
        newTools = newTools.filter(
          (t) => !(t.type === "function" && t.function?.name === toolId)
        );
      } else {
        // Add the function tool

        // Look up tool in registry
        const tool = TOOL_REGISTRY[toolId];

        if (tool) {
          // Tool found in registry
          newTools.push({
            type: "function",
            function: {
              name: toolId,
              description: tool.description,
              parameters:
                tool?.definition?.function?.parameters ||
                tool?.function?.parameters,
            },
          });
        } else if (toolId === "file_search" || toolId === "code_interpreter") {
          // Built-in tool
          newTools.push({ type: toolId });
        } else {
          // Unknown function tool - create a default function definition
          newTools.push({
            type: "function",
            function: {
              name: toolId,
              description: `Function to ${toolId
                .replace(/([A-Z])/g, " $1")
                .replace(/([A-Z][a-z])/g, " $1")
                .toLowerCase()
                .replace(/tool$/, "")}`,
              parameters: {
                type: "object",
                properties: {},
                required: [],
              },
            },
          });
        }
      }

      // Create metadata for update
      const toolNames = newTools.map((t) =>
        t.type === "function" ? t.function.name : t.type
      );

      const metadata = {
        ...(currentAssistant.metadata || {}),
        tool_names: JSON.stringify(toolNames),
        tool_count: String(newTools.length),
        updated_at: new Date().toISOString(),
      };

      // Create update payload
      const updatePayload = {
        tools: newTools,
        metadata: metadata,
      };

      // Update via store function
      const updatedAssistant = await updateAssistantTools(
        assistantId,
        updatePayload
      );

      // Update local state
      setTools(updatedAssistant.tools);
      setValue("tools", updatedAssistant.tools);

      return updatedAssistant;
    } catch (error) {
      console.error("Tool toggle error:", error);
      throw error;
    }
  };

  const handleFileUpdate = async (newFiles, toolType) => {
    try {
      // Simplified file handling since we no longer use OpenAI Assistants API
      // Files are now handled through our backend and Supabase storage

      if (toolType === "file_search") {
        setFiles((prev) => ({
          ...prev,
          vector: [...prev.vector, ...newFiles],
        }));
      } else if (toolType === "code_interpreter") {
        setFiles((prev) => ({
          ...prev,
          codeInterpreter: [...prev.codeInterpreter, ...newFiles],
        }));
      }

      // Note: Actual file processing and storage should be handled by the backend
      // This is just updating the UI state for display purposes
    } catch (error) {
      console.error("Error updating files:", error);
    }
  };

  const handleFunctionSave = async (functionConfig) => {
    try {
      if (!functionConfig.type || !functionConfig.function) {
        throw new Error("Invalid function configuration");
      }

      // Store current instructions
      const currentInstructions =
        pendingInstructions ||
        formValues.instructions ||
        assistant?.instructions;

      const updatedTools = [...tools, functionConfig];
      await updateAssistantTools(currentAssistant.id, updatedTools);

      setTools(updatedTools);
      setDialogState((prev) => ({ ...prev, functionWriter: false }));
    } catch (error) {
      console.error("Error adding function:", error);
    }
  };

  const handleInstructionsGenerated = async (instructions) => {
    try {
      setPendingInstructions(instructions);
      setValue("instructions", instructions);
      setHasUnsavedChanges(true);
      setIsGeneratingInstructions(false);
    } catch (error) {
      console.error("Error updating instructions:", error);
    } finally {
      setIsGeneratingInstructions(false);
    }
  };

  const onSubmit = async (data) => {
    try {
      // This is now just a wrapper around saveAllChanges
      await saveAllChanges();
    } catch (error) {
      console.error("Error updating assistant:", error);
    }
  };

  // Modified function to suggest tools while preserving instructions
  const suggestToolsFromInstructions = async () => {
    try {
      setIsSuggestingTools(true);

      // Use the most up-to-date instructions from pending or form values
      const instructions = pendingInstructions || formValues.instructions;

      if (!instructions || instructions.trim().length < 20) {
        setIsSuggestingTools(false);
        return;
      }

      // Get backend API base URL
      const API_BASE_URL =
        import.meta.env.VITE_API_BASE_URL || "http://localhost:3001";

      // Use the backend API to suggest tools
      const response = await fetch(
        `${API_BASE_URL}/api/ai/assistant/suggest-tools`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            instructions,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.error || `HTTP ${response.status}: ${response.statusText}`
        );
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Failed to suggest tools");
      }

      // Process the suggested tools - backend returns { recommendedTools, reasoning }
      const parsedTools = {
        recommended_tools: result.recommendedTools,
        reasoning: result.reasoning,
      };

      // Validate the structure and tool count
      if (
        !parsedTools.recommended_tools ||
        !Array.isArray(parsedTools.recommended_tools)
      ) {
        throw new Error(
          "Invalid response format: missing or invalid recommended_tools array"
        );
      }

      // Verify tool count is within expected range
      const recommendedToolCount = parsedTools.recommended_tools.length;

      if (recommendedToolCount < 2 || recommendedToolCount > 8) {
        console.warn(
          `Warning: AI suggested ${recommendedToolCount} tools, which is outside the expected range of 2-8.`
        );
      }

      // Check if reasoning exists
      if (!parsedTools.reasoning || typeof parsedTools.reasoning !== "string") {
        console.warn("Warning: Missing or invalid reasoning in response");
        parsedTools.reasoning = "No reasoning provided";
      }

      // Update the assistant with the suggested tools
      const existingToolNames = tools.map((tool) =>
        tool.type === "function" ? tool.function.name : tool.type
      );

      // Filter out tools that are already attached
      const newToolNames = parsedTools.recommended_tools.filter(
        (toolName) => !existingToolNames.includes(toolName)
      );

      if (newToolNames.length === 0) {
        setIsSuggestingTools(false);
        return;
      }

      // Create tool structures from tool names
      let newTools = [...tools];
      const toolsAdded = [];

      for (const toolName of newToolNames) {
        if (toolName === "file_search" || toolName === "code_interpreter") {
          newTools.push({ type: toolName });
          toolsAdded.push(toolName);
        } else {
          // Try to get tool from registry
          const tool = TOOL_REGISTRY[toolName];

          if (tool) {
            newTools.push({
              type: "function",
              function: {
                name: toolName,
                description: tool.description || tool.function?.description,
                parameters: tool.function?.parameters || {
                  type: "object",
                  properties: {},
                  required: [],
                },
              },
            });
            toolsAdded.push(toolName);
          } else {
            console.warn(`Tool not found in registry: ${toolName}`);
            // Try a direct approach for common tools that might not be in the registry
            newTools.push({
              type: "function",
              function: {
                name: toolName,
                description: `Function to ${toolName
                  .replace(/([A-Z])/g, " $1")
                  .replace(/([A-Z][a-z])/g, " $1")
                  .toLowerCase()
                  .replace(/tool$/, "")}`,
                parameters: {
                  type: "object",
                  properties: {},
                  required: [],
                },
              },
            });
            toolsAdded.push(toolName);
          }
        }
      }

      // Update assistant with new tools
      // Use current form values as a base to ensure we don't reset anything
      const assistantToUpdate = {
        // Use pending values first, then form values, then fall back to assistant object
        name: pendingName || formValues.name || currentAssistant?.name,
        description:
          pendingDescription ||
          formValues.description ||
          currentAssistant?.description,
        instructions:
          pendingInstructions ||
          formValues.instructions ||
          currentAssistant?.instructions,
        model: formValues.model || currentAssistant?.model,
        tools: newTools,
        metadata: {
          ...(currentAssistant?.metadata || {}),
          tool_names: JSON.stringify(
            newTools.map((tool) =>
              tool.type === "function" ? tool.function.name : tool.type
            )
          ),
          tool_count: String(newTools.length),
          updated_at: new Date().toISOString(),
        },
      };

      const openaiAssistantId = await (async () => {
        // Get the assistant ID (should be Supabase UUID)
        return currentAssistant.id;
      })();

      if (!openaiAssistantId) {
        throw new Error("No valid assistant ID found");
      }

      // Build an array of actual tool objects from the tool names
      const newToolObjects = [];

      // Process each new tool
      for (const toolName of newToolNames) {
        // Check if it's a built-in tool
        if (
          toolName === "code_interpreter" ||
          toolName === "file_search" ||
          toolName === "retrieval"
        ) {
          newToolObjects.push({ type: toolName });
        } else {
          // Look up the function tool in registry
          const tool = TOOL_REGISTRY[toolName];
          if (tool) {
            newToolObjects.push({
              type: "function",
              function: {
                name: toolName,
                description: tool.description,
                parameters:
                  tool?.definition?.function?.parameters ||
                  tool?.function?.parameters,
              },
            });
          } else {
            // For custom/unknown tools, create a default definition
            newToolObjects.push({
              type: "function",
              function: {
                name: toolName,
                description: `Function to ${toolName
                  .replace(/([A-Z])/g, " $1")
                  .replace(/([A-Z][a-z])/g, " $1")
                  .toLowerCase()
                  .replace(/Tool$/, "")}`,
                parameters: {
                  type: "object",
                  properties: {},
                  required: [],
                },
              },
            });
          }
        }
      }

      // Combine existing and new tools
      const combinedTools = [...tools, ...newToolObjects];

      // Extract tool names for metadata (will be properly stringified)
      const combinedToolNames = combinedTools.map((tool) =>
        tool.type === "function" ? tool.function.name : tool.type
      );

      // Create update payload with properly formatted metadata
      // Start with the current form values and assistant's metadata
      const metadata = {
        ...currentAssistant.metadata,
        tool_names: JSON.stringify(combinedToolNames),
        tool_count: String(combinedTools.length),
        updated_at: new Date().toISOString(),
      };

      // Create the update payload with ALL form data preserved
      const updatePayload = {
        name: pendingName || formValues.name || currentAssistant?.name,
        description:
          pendingDescription ||
          formValues.description ||
          currentAssistant?.description,
        instructions:
          pendingInstructions ||
          formValues.instructions ||
          currentAssistant?.instructions,
        model: formValues.model || currentAssistant?.model,
        tools: combinedTools,
        metadata: metadata,
      };

      // STEP 1: Update via the assistantStore to keep local state in sync
      await updateAssistantTools(openaiAssistantId, updatePayload);

      // STEP 2: Find Supabase ID for direct database update
      let supabaseId = null;

      // Try multiple methods to find the Supabase ID
      if (currentAssistant?.id && !currentAssistant.id.startsWith("asst_")) {
        supabaseId = currentAssistant.id;
      } else if (currentAssistant?.metadata?.id) {
        supabaseId = currentAssistant.metadata.id;
      } else if (currentAssistant?.metadata?.supabase_id) {
        supabaseId = currentAssistant.metadata.supabase_id;
      }

      // STEP 3: Update Supabase directly if we have an ID
      if (supabaseId) {
        try {
          // Import supabase directly
          const { supabase } = await import("@/lib/supabase");

          // Create update payload for Supabase that preserves ALL form data
          const supabaseUpdateData = {
            updated_at: new Date().toISOString(),
          };

          // Add the fields being updated
          if (updatePayload.name !== undefined) {
            supabaseUpdateData.name = updatePayload.name;
          }

          if (updatePayload.description !== undefined) {
            supabaseUpdateData.description = updatePayload.description;
          }

          if (updatePayload.instructions !== undefined) {
            supabaseUpdateData.instructions = updatePayload.instructions;
          }

          // Always update metadata
          supabaseUpdateData.metadata = {
            ...(currentAssistant?.metadata || {}),
            updated_at: new Date().toISOString(),
            user_id: userId,
          };

          const { data, error } = await supabase
            .from("assistants")
            .update(supabaseUpdateData)
            .eq("id", supabaseId)
            .select()
            .single();

          if (error) {
            console.error("Error updating Supabase with tools:", error);
          }
        } catch (supabaseError) {
          console.error(
            "Exception updating Supabase with tools:",
            supabaseError
          );
          // Don't throw, just log the error
        }
      }

      // Update local state with new tools
      setTools(combinedTools);
      setValue("tools", combinedTools);

      // Also update currentAssistant in the store to ensure UI is consistent
      setCurrentAssistant({
        ...currentAssistant,
        name: pendingName || formValues.name || currentAssistant?.name,
        description:
          pendingDescription ||
          formValues.description ||
          currentAssistant?.description,
        instructions:
          pendingInstructions ||
          formValues.instructions ||
          currentAssistant?.instructions,
        model: formValues.model || currentAssistant?.model,
        tools: combinedTools,
        metadata: {
          ...currentAssistant.metadata,
          tool_names: JSON.stringify(combinedToolNames),
          tool_count: String(combinedTools.length),
          updated_at: new Date().toISOString(),
        },
      });

      setIsSuggestingTools(false);
      return parsedTools;
    } catch (error) {
      console.error("Error suggesting tools:", error);
      setIsSuggestingTools(false);
      return null;
    }
  };

  return {
    // Form setup
    control,
    errors,
    isDirty,
    isSaving,
    formValues,
    formState: assistant,
    tools,
    files,

    // UI state
    isGeneratingInstructions,
    dialogState,
    isEditingInstructions,
    isSuggestingTools,
    hasUnsavedChanges,

    // Handlers
    handleFieldUpdate,
    handleToolToggle,
    handleFileUpdate,
    handleFunctionSave,
    handleInstructionsGenerated,
    onSubmit,
    reset,
    saveAllChanges,
    suggestToolsFromInstructions,

    // Dialog controls
    setDialogState,
    setIsEditingInstructions,
    setIsGeneratingInstructions,
    setHasUnsavedChanges,
    handleUpdateFiles: handleFileUpdate,
  };
}

export default useAssistantForm;
